import { Controller } from "@hotwired/stimulus";
import * as responses from "utils/responses";
import * as http from "utils/http";
import { displayServerErrors } from "controllers/forms/errors";
import * as notifications from "utils/notifications";
import { preventButtonDoubleClick } from "utils/dom";
import * as loadingTemplate from "grid/overlayLoadingTemplate";

export type BankAccountType = "CHECKING" | "CORPORATE_CHECKING" | "SAVINGS" | "";

export default class extends Controller {
  static targets = [
    "accountNumber",
    "bankName",
    "bankNameDetails",
    "email",
    "holderName",
    "routingNumber",
    "submitButton",
    "type",
    "validationErrors",
  ];

  declare readonly accountNumberTarget: HTMLInputElement;
  declare readonly emailTarget: HTMLInputElement;
  declare readonly holderNameTarget: HTMLInputElement;
  declare readonly routingNumberTarget: HTMLInputElement;
  declare readonly submitButtonTarget: HTMLAnchorElement;
  declare readonly typeTargets: HTMLInputElement[];
  declare readonly validationErrorsTarget: HTMLSpanElement;
  declare readonly bankNameDetailsTarget: HTMLSpanElement;
  declare readonly bankNameTarget: HTMLLabelElement;

  connect() {
    this.routingNumberTarget.addEventListener("blur", this.handleRoutingNumberBlur.bind(this));
    this.submitButtonTarget.addEventListener(
      "click",
      preventButtonDoubleClick(this.submitButtonTarget, this.submitBankingProfile.bind(this)),
    );
  }

  disconnect() {
    this.submitButtonTarget.removeEventListener(
      "click",
      preventButtonDoubleClick(this.submitButtonTarget, this.submitBankingProfile.bind(this)),
    );
    this.routingNumberTarget.removeEventListener("blur", this.handleRoutingNumberBlur.bind(this));
  }

  closeModal() {
    this.element.remove();
  }

  async submitBankingProfile(event: MouseEvent) {
    event.preventDefault();
    this.validationErrorsTarget.innerHTML = "";
    const body = {
      banking_profile: this.formToParams(),
    };
    const response = await http.httpPost(this.submitButtonTarget.href, body);
    const data = await response.json();
    if (response.ok) {
      this.closeModal();
      this.purgeCacheInBankingProfilesController();
      notifications.success(data.success);
    } else {
      displayServerErrors(data.error, this.validationErrorsTarget, this.fieldToInputMap, this.application);
    }
  }

  handleRoutingNumberBlur() {
    if (this.routingNumberTarget.value.trim() !== "") {
      this.getBankName();
    }
  }

  async getBankName() {
    this.bankNameTarget.innerHTML = loadingTemplate.build(loadingTemplate.Size.FORM);
    const response = await http.httpGet(this.bankNameDetailsTarget.getAttribute("get-bank-name-url"), {
      routing_number: this.routingNumberTarget.value,
    });
    if (response.ok) {
      this.bankNameTarget.innerHTML = (await response.json()).bank_name;
    } else {
      this.bankNameTarget.innerHTML = await responses.parseError(response);
    }
  }

  private purgeCacheInBankingProfilesController() {
    const bankingProfilesElement = document.querySelector('[data-controller="banking-profiles--banking-profiles"]');
    const bankingProfilesController = this.application.getControllerForElementAndIdentifier(
      bankingProfilesElement,
      "banking-profiles--banking-profiles",
    ) as any;
    bankingProfilesController.purgeCacheAndRefreshTable();
  }

  private formToParams() {
    const params: Record<string, unknown> = {};
    Object.entries(this.fieldToInputMap).forEach(([field, html]) => {
      if (field === "type") {
        const checkedValue =
          Array.from(html)
            .map((div) => div.querySelector(".form-radio"))
            .find((radio) => radio?.checked)?.value || "";
        params[field] = checkedValue as BankAccountType;
      } else {
        params[field] = (html as HTMLInputElement).value;
      }
    });

    const queryParams = new URLSearchParams(window.location.search);
    params["alfa_account_number"] = queryParams.get("alfa_account_number");
    params["alfa_customer_reference"] = queryParams.get("alfa_customer_reference");

    return params;
  }

  get fieldToInputMap() {
    return {
      account_number: this.accountNumberTarget,
      email: this.emailTarget,
      holder_name: this.holderNameTarget,
      routing_number: this.routingNumberTarget,
      type: this.typeTargets,
    } as Record<string, HTMLInputElement | HTMLDivElement[]>;
  }
}
